import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import API_ENDPOINTS from "../apiConfig";
import { downloadViaAuthFetch } from "../utils/downloadFile";
import { getLanguage } from "../utils/localStorageHelper";
import PassingsExportForm from "./PassingsExportForm";

// Site settings → Export tab. Plates are exported one area per file on purpose.
function SiteExport({ site, areas }) {
  const { t } = useTranslation();

  const [plateAreaId, setPlateAreaId] = useState(areas.length === 1 ? String(areas[0].id) : "");
  const [exportingPlates, setExportingPlates] = useState(false);
  const [plateError, setPlateError] = useState("");
  const [passingsAreaId, setPassingsAreaId] = useState("all");

  const handlePlateExport = async () => {
    setExportingPlates(true);
    setPlateError("");
    try {
      const url = new URL(`${API_ENDPOINTS.getareas}/${plateAreaId}/license-plates/export`, window.location.origin);
      url.searchParams.set("lang", getLanguage()); // German spreadsheets expect ';' — see LicensePlateController::exportCsv

      const area = areas.find((a) => String(a.id) === plateAreaId);
      const safe = (s) => (s || "").replace(/[^a-z0-9]+/gi, "_");
      const filename = `plates_${safe(site.name)}_${safe(area?.name)}_${new Date().toISOString().split("T")[0]}.csv`;

      await downloadViaAuthFetch(url.toString(), filename);
    } catch (e) {
      setPlateError(t("export_failed"));
    } finally {
      setExportingPlates(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div id="export-plates-box" className="p-4 border ez-rounded-card bg-white shadow-sm">
        <h6 className="mb-1"><i className="bi bi-card-list me-2"></i>{t("export_plates_title")}</h6>
        <p className="text-muted small mb-3">{t("export_plates_hint")}</p>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6">
            <label className="form-label small text-muted mb-1" htmlFor="plates-export-area">{t("Area")}</label>
            <select
              id="plates-export-area"
              className="form-select form-select-sm"
              value={plateAreaId}
              onChange={(e) => setPlateAreaId(e.target.value)}
            >
              <option value="" disabled>{t("export_select_area")}</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <button
              className="btn btn-sm ez-button-full w-100"
              onClick={handlePlateExport}
              disabled={!plateAreaId || exportingPlates}
            >
              {exportingPlates ? t("Loading...") : (
                <><i className="bi bi-download me-1"></i>{t("export_csv")}</>
              )}
            </button>
          </div>
        </div>
        {plateError && <div className="text-danger small mt-2">{plateError}</div>}
      </div>

      <div id="export-passings-box" className="p-4 border ez-rounded-card bg-white shadow-sm">
        <h6 className="mb-1"><i className="bi bi-bar-chart me-2"></i>{t("export_passings_title")}</h6>
        <p className="text-muted small mb-3">{t("export_passings_hint")}</p>
        <PassingsExportForm
          siteId={site.id}
          siteName={site.name}
          areas={areas}
          areaId={passingsAreaId}
          onAreaChange={setPassingsAreaId}
        />
      </div>
    </div>
  );
}

export default SiteExport;
